import useTrackScreenDualFire from '../useTrackScreenDualFire/useTrackScreenDualFire';
import {
  AppTrackingService,
  TrackEventParams,
} from '../useTrackScreenDualFire/useTrackScreenDualFire.types';
import { UseTrackScreen } from './createUseTrackScreen.types';

/**
 * Binds an app's tracking service to the shared dual-fire hook, so every screen
 * in that app reports itself in one line: `useTrackScreen('park', 'Park: Opened')`.
 *
 * Both type arguments are given explicitly, because the app already owns both
 * and inference cannot supply one without the other:
 *
 * ```ts
 * const useTrackScreen = createUseTrackScreen<ScreenName, TrackingEvents>(
 *   trackingService,
 * );
 * ```
 *
 * The legacy event is optional: an app is not uniformly one or the other. uai
 * reports `ScreenViewed` from its main screen and nothing from its two error
 * screens; a greenfield app has none at all. Omit it only where no legacy event
 * exists — inventing one would mean growing the closed allowlist.
 *
 * `ScreenName` is the app's union of catalogued `registries.screen_name.<app>`
 * values — that union is what makes a typo fail the build rather than ship a
 * screen nobody queries. `TrackingEvents` keeps the legacy event and its payload
 * checked exactly as a direct `trackingService.trackEvent` call would be.
 *
 * This exists because the alternative was 16 hand-copied wrappers, and hand-copied
 * tracking wrappers are precisely what produced the two defects FE-2691 found (one
 * app emitting a *different* app's prefix, another nesting every property under
 * `params`). One implementation, one place to fix.
 */
export default function createUseTrackScreen<
  TScreenName extends string,
  TEvents,
>(trackingService: AppTrackingService<TEvents>) {
  // The implementation signature is the loose one, as overloads require; the
  // precise pair lives in `UseTrackScreen`.
  const useTrackScreen: UseTrackScreen<TScreenName, TEvents> = (
    screenName: TScreenName | null | undefined,
    legacyEvent?: keyof TEvents & string,
    ...params: unknown[]
  ) =>
    useTrackScreenDualFire(
      trackingService,
      screenName,
      legacyEvent,
      ...(params as TrackEventParams<TEvents, keyof TEvents & string>),
    );

  return useTrackScreen;
}
